import { useDocItems } from '@modules/docs/providers/hooks/useDocItems';
import { useDownload } from '@modules/file-menagement';
import { List, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import Iconify from 'src/components/iconify/Iconify';
import { SkeletonConversationItem } from 'src/components/skeleton';
import {
  NavAccordionDetails,
  NavAccordionLabel,
  NavAccordionSummary,
} from 'src/features/Navbar/NavAccordion/NavAccordion';
import { NavAccordionPersisted } from 'src/features/Navbar/NavAccordionPersisted';
import { NavActionButton } from 'src/features/Navbar/NavActionButton/NavActionButton';
import { DocsTrashPopover } from 'src/features/doc/DocsTrashPopover';
import { useLocales } from 'src/locales';
import {
  DocsTreeView,
  useDocImportDialog,
  useDocMutation,
} from 'src/pages/dashboard/DocsPage';
import { DocsTreeContainer } from 'src/pages/dashboard/DocsPage/shared/DocsTreeView';
import { FavoritesDocsNav } from 'src/pages/dashboard/DocsPage/shared/FavoritesDocsNav';
import { useDocExportMutation } from 'src/pages/dashboard/DocsPage/shared/useDocExportMutation';
import { UIFeature } from 'src/state/ui/types';
import { FormatTime } from 'src/utils/formatTime';
import { MoreNavItensButton } from '../../shared/MoreNavItensButton';
import { NavRootMenuItem } from '../../shared/styles';
import { useDocsNavMoreDrawer } from './DocsNavMoreDrawer';

export const DocsNav = () => {
  const { docs, isDocsLoading } = useDocItems();
  const { downloadContent, loadingMap } = useDownload();

  const navigate = useNavigate();
  const { translate } = useLocales();

  const { createDoc } = useDocMutation();
  const { exportDoc } = useDocExportMutation();

  const { showDocImport } = useDocImportDialog();
  const { showDocsNavMoreDrawer } = useDocsNavMoreDrawer();

  const [isTrashOpened, setIsTrashOpened] = useState<boolean>(false);

  const handleCreateDoc = async () => {
    const docReturn = await createDoc({});

    if (docReturn) {
      navigate(PATH_DASHBOARD.docs.view(docReturn._id));
    }
  };
  const fileName = `copera-docs-${FormatTime.Date(new Date(), 'dd-MM-yyyy')}.dcp`;

  const handleExport = async () => {
    const blob = await exportDoc('');
    const file = new File([blob], fileName);
    downloadContent({ fileName, blob: file });
  };

  const nonFavDocs = useMemo(() => docs?.filter((doc) => !doc.starred), [docs]);

  const hasMoreThanEightParentDocs =
    (docs ?? []).filter((doc) => !doc.noteRef && !doc.parent).length > 8;

  return (
    <NavAccordionPersisted feature={UIFeature.docs}>
      <NavAccordionSummary className="docs">
        <NavAccordionLabel>
          <Iconify className="navIcon" icon="tabler:file-description-filled" />
          <Typography variant="subtitle2">
            {translate('header.nav_vertical.board_tab.docs')}
          </Typography>
        </NavAccordionLabel>
        <Stack direction="row" gap={1}>
          <Tooltip title={translate('collaborate.docs.export_all')}>
            <span>
              <NavActionButton
                icon="tabler:download"
                loading={loadingMap[fileName]}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExport();
                }}
              />
            </span>
          </Tooltip>
          <Tooltip title={translate('collaborate.docs.import_a_document')}>
            <span>
              <NavActionButton
                icon="tabler:upload"
                onClick={(e) => {
                  e.stopPropagation();
                  showDocImport();
                }}
              />
            </span>
          </Tooltip>
          <NavActionButton
            icon="tabler:plus"
            onClick={(e) => {
              e.stopPropagation();
              handleCreateDoc();
            }}
          />
        </Stack>
      </NavAccordionSummary>
      <NavAccordionDetails>
        {isDocsLoading && (
          <List disablePadding>
            <SkeletonConversationItem />
            <SkeletonConversationItem />
            <SkeletonConversationItem />
            <SkeletonConversationItem />
            <SkeletonConversationItem />
          </List>
        )}
        <FavoritesDocsNav />

        <DocsTreeContainer>
          {!isDocsLoading && nonFavDocs && (
            <DocsTreeView docs={nonFavDocs} isFromNav />
          )}
        </DocsTreeContainer>

        <Stack px={1.5}>
          {docs && hasMoreThanEightParentDocs && (
            <MoreNavItensButton
              onClick={showDocsNavMoreDrawer}
              isTreeview={true}
            />
          )}
          <PopoverTrigger
            popover={DocsTrashPopover}
            isOpened={(e) => setIsTrashOpened(e)}
          >
            <NavRootMenuItem
              selected={isTrashOpened}
              className="isTreeview"
              sx={{ marginBottom: 1.5 }}
            >
              <Iconify icon="tabler:trash" color="text.secondary" />
              <ListItemText
                secondary={translate('collaborate.docs.trash.title')}
              />
            </NavRootMenuItem>
          </PopoverTrigger>
        </Stack>
      </NavAccordionDetails>
    </NavAccordionPersisted>
  );
};
